<template>
  <el-dialog
      title="更新登录信息"
      v-model="dialogVisible"
      width="500px"
      @close="handleCancel"
  >
    <div class="dialog-content">
      <p class="company-name">{{ companyName }}</p>

      <div v-if="loginType === 1">
        <el-form :model="formData" label-position="top">
          <el-form-item label="短信验证码" prop="smsCode">
            <el-input
                v-model="formData.smsCode"
                placeholder="请输入短信验证码"
                :disabled="!smsInputEnabled"

            />
          </el-form-item>
        </el-form>
      </div>

      <!-- 二维码登录类型 -->
      <div v-else-if="loginType === 2">
        <div class="qr-code-container">
          <div class="qr-code-wrapper">
            <img v-if="qrCodeImg" :src="qrCodeImg" alt="登录二维码" class="qr-code"/>
            <el-skeleton v-else animated>
              <template #template>
                <div class="qr-code-skeleton"></div>
              </template>
            </el-skeleton>
          </div>
          <div class="update-time" v-if="updateTime">
            更新时间：{{ formatTime(updateTime) }}
          </div>
        </div>
      </div>
    </div>


    <div v-if="loginType === 3">
      <el-form :model="formData" label-position="top">
        <img  @click="fetchCaptcha"  v-if="captchaImg" :src="captchaImg" alt="图片验证码" class="captcha-code"/>
        <el-form-item label="验证码" prop="verifyCode">
          <el-input
              v-model="formData.verifyCode"
              placeholder="请输入验证码"
              :disabled="!smsInputEnabled"

          />
        </el-form-item>
      </el-form>
    </div>

    <template #footer>
      <!-- 短信验证码登录类型按钮 -->
      <template v-if="loginType === 1">
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" @click="handleUpdate" :loading="loading">
          {{ loading ? '更新中...' : (smsInputEnabled ? '继续更新' : '更新') }}
        </el-button>
      </template>

      <!-- 二维码登录类型按钮 -->
      <template v-else-if="loginType === 2">
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" @click="handleRefreshQrCode" :loading="loading">
          {{ loading ? '刷新中...' : '刷新二维码' }}
        </el-button>
      </template>

      <template v-if="loginType === 3">
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" @click="handleUpdateVerifyCode" :loading="loading">
          {{ loading ? '更新中...' : '更新' }}
        </el-button>
      </template>
    </template>
  </el-dialog>
</template>

<script setup>
import {ref, reactive, watch, onMounted} from 'vue'
import {ElMessage} from 'element-plus'
import {postWlApi} from '@utils/api_v1.js'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  loginType: {
    type: Number,
    required: true
  },
  companyId: {
    type: Number,
    required: true
  },
  companyName: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['close', 'success'])

const formData = reactive({
  smsCode: '', verifyCode: ''
})

const dialogVisible = ref(props.visible)
const loading = ref(false)
const smsInputEnabled = ref(false)
const qrCodeImg = ref('')
const captchaImg = ref('')
const updateTime = ref('')

// 监听visible属性变化，控制对话框显示
watch(() => props.visible, (newVal) => {
  dialogVisible.value = newVal
})

// 监听dialogVisible变化，当对话框关闭时触发close事件
watch(dialogVisible, (newVal) => {
  if (!newVal) {
    resetState()
    emit('close')
  }
})

// 组件挂载时
onMounted(() => {
  if (props.loginType === 2 && props.visible) {
    fetchQrCode()
  }

  if (props.loginType === 3) {
    smsInputEnabled.value = true
    fetchCaptcha()
  }


})

// 重置状态
const resetState = () => {
  formData.smsCode = ''
  loading.value = false
  smsInputEnabled.value = false
  qrCodeImg.value = ''
  updateTime.value = ''

  // 清除轮询计时器
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

// 格式化时间
const formatTime = (timeStr) => {
  if (!timeStr) return ''
  try {
    const date = new Date(timeStr)
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  } catch (error) {
    return timeStr
  }
}

// 轮询计时器
let pollTimer = null
let pollStartTime = 0
const POLL_DURATION = 60000 // 1分钟

// 请求二维码
const fetchQrCode = async () => {
  loading.value = true
  try {
    const response = await postWlApi('/refresh_access', {
      company_id: props.companyId
    })

    if (response.code === 0) {
      qrCodeImg.value = response.data.pending_qrcode.qr_image
      updateTime.value = response.data.update_time

      // 开始轮询登录状态
      startPolling()
    } else {
      ElMessage.error(response.message || '获取二维码失败')
    }
  } catch (error) {
    console.error('获取二维码失败:', error)
    ElMessage.error('获取二维码失败，请稍后重试')
  } finally {
    loading.value = false
  }
}


// 请求二维码
const fetchCaptcha = async () => {
  loading.value = true
  try {
    const response = await postWlApi('/refresh_access', {
      company_id: props.companyId
    })

    if (response.code === 0) {

      captchaImg.value = response.data.captcha_data_uri


    } else {
      ElMessage.error(response.message || '获取验证码失败')
    }
  } catch (error) {
    console.error('获取验证码失败:', error)
    ElMessage.error('获取验证码失败，请稍后重试')
  } finally {
    loading.value = false
  }
}

// 开始轮询登录状态
const startPolling = () => {
  // 清除之前的计时器
  if (pollTimer) {
    clearInterval(pollTimer)
  }

  pollStartTime = Date.now()

  // 立即检查一次
  checkLoginStatus()

  // 每3秒检查一次
  pollTimer = setInterval(() => {
    // 检查是否超过1分钟
    if (Date.now() - pollStartTime > POLL_DURATION) {
      clearInterval(pollTimer)
      ElMessage.warning('二维码已过期，请刷新二维码')
      dialogVisible.value = false

      return
    }

    checkLoginStatus()
  }, 5000)
}

// 检查登录状态
const checkLoginStatus = async () => {
  try {
    const response = await postWlApi('/validate_access', {
      company_id: props.companyId
    })

    if (response.code === 0 && response.data.is_valid === true) {
      // 登录成功
      clearInterval(pollTimer)
      ElMessage.success('登录成功')
      emit('success')
      dialogVisible.value = false
    }
  } catch (error) {
    console.error('检查登录状态失败:', error)
  }
}

// 刷新二维码
const handleRefreshQrCode = async () => {
  await fetchQrCode()
}

// 更新登录信息（短信验证码类型）
const handleUpdate = async () => {
  // 如果需要验证码且输入框不可用，先发送请求
  if (!smsInputEnabled.value) {
    loading.value = true
    try {
      const response = await postWlApi('/refresh_access', {
        company_id: props.companyId
      })

      if (response.code === 0) {
        ElMessage.success('更新成功')
        emit('success')
        dialogVisible.value = false
      } else if (response.code === 510) {
        // 需要验证码，启用输入框
        smsInputEnabled.value = true
        ElMessage.warning('请输入验证码继续')
      } else {
        ElMessage.error(response.message || '更新失败')
      }
    } catch (error) {
      console.error('更新登录信息失败:', error)
      ElMessage.error('更新失败，请稍后重试')
    } finally {
      loading.value = false
    }
  }
  // 输入框可用，携带验证码继续更新
  else {
    if (!formData.smsCode.trim()) {
      ElMessage.error('请输入验证码')
      return
    }

    loading.value = true
    try {
      const response = await postWlApi('/refresh_access', {
        company_id: props.companyId,
        sms_code: formData.smsCode.trim()
      })

      if (response.code === 0) {
        ElMessage.success('更新成功')
        emit('success')
        dialogVisible.value = false
      } else {
        ElMessage.error(response.message || '更新失败')
      }
    } catch (error) {
      console.error('更新登录信息失败:', error)
      ElMessage.error('更新失败，请稍后重试')
    } finally {
      loading.value = false
    }
  }
}


// 更新登录信息（短信验证码类型）
const handleUpdateVerifyCode = async () => {
  // 如果需要验证码且输入框不可用，先发送请求

  if (!formData.verifyCode.trim()) {
    ElMessage.error('请输入验证码')
    return
  }

  loading.value = true
  try {
    const response = await postWlApi('/refresh_access', {
      company_id: props.companyId,
      verify_code: formData.verifyCode.trim()
    })

    if (response.code === 0) {
      ElMessage.success('更新成功')
      emit('success')
      dialogVisible.value = false
    } else {
      ElMessage.error(response.message || '更新失败')
    }
  } catch (error) {
    console.error('更新登录信息失败:', error)
    ElMessage.error('更新失败，请稍后重试')
  } finally {
    loading.value = false
  }

}

// 取消操作
const handleCancel = () => {
  dialogVisible.value = false
}
</script>

<style scoped>
.dialog-content {
  padding: 0;
}

.company-name {
  font-size: 16px;
  color: #303133;
  margin-bottom: 20px;
  font-weight: bold;
}

/* 二维码样式 */
.qr-code-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.qr-code-wrapper {
  width: 200px;
  height: 200px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px;
}

.qr-code {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.captcha-code {
  width: 200px;
  object-fit: contain;
}

.qr-code-skeleton {
  width: 100%;
  height: 100%;
  border-radius: 4px;
}

.update-time {
  font-size: 14px;
  color: #606266;
  margin-top: 8px;
}
</style>
